import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  DEFAULT_CUT, DEFAULT_MARGINS, DEFAULT_OUTPUT, DEFAULT_PAPER, DEFAULT_SIGN,
  DEFAULT_TILE, DEFAULT_VIEW,
} from "./defaults";
import { buildLayout, computeFitScale, contentSize } from "../domain/layout";
import { NAVBAR_HEIGHT } from "../domain/insets";
import { clamp } from "../domain/units";
import type {
  CutConfig, MarginsConfig, OutputConfig, PaperConfig, Pan, SignConfig,
  Source, TileConfig, ViewMode, Zoom,
} from "../domain/types";

export interface BusyState {
  active: boolean;
  text: string;
}

export interface AppState {
  // persistent
  paper: PaperConfig;
  margins: MarginsConfig;
  sign: SignConfig;
  tile: TileConfig;
  cut: CutConfig;
  output: OutputConfig;
  view: ViewMode;

  // ephemeral
  source: Source | null;
  zoom: Zoom;
  pan: Pan;
  busy: BusyState;
  hostSize: { w: number; h: number };

  // setters
  setPaper: (patch: Partial<PaperConfig>) => void;
  setMargins: (patch: Partial<MarginsConfig>) => void;
  setSign: (patch: Partial<SignConfig>) => void;
  setTile: (patch: Partial<TileConfig>) => void;
  setCut: (patch: Partial<CutConfig>) => void;
  setOutput: (patch: Partial<OutputConfig>) => void;
  setView: (view: ViewMode) => void;

  setSource: (source: Source | null) => void;
  setZoom: (zoom: Zoom) => void;
  setPan: (pan: Pan) => void;
  resetViewport: () => void;
  setHostSize: (size: { w: number; h: number }) => void;
  zoomAt: (factor: number, mx: number, my: number) => void;
  zoomCentered: (factor: number) => void;

  setBusy: (text: string | null) => void;

  resetAll: () => void;
}

const PERSIST_KEY = "ss.state.v1";

export const useApp = create<AppState>()(
  persist(
    (set, get) => ({
      paper:   { ...DEFAULT_PAPER },
      margins: { ...DEFAULT_MARGINS },
      sign:    { ...DEFAULT_SIGN },
      tile:    { ...DEFAULT_TILE },
      cut:     { ...DEFAULT_CUT },
      output:  { ...DEFAULT_OUTPUT },
      view:    DEFAULT_VIEW,

      source: null,
      zoom: "fit",
      pan: { x: 0, y: 0 },
      busy: { active: false, text: "" },
      hostSize: { w: 0, h: 0 },

      setPaper:   (patch) => set((s) => ({ paper:   { ...s.paper,   ...patch } })),
      setMargins: (patch) => set((s) => ({ margins: { ...s.margins, ...patch } })),
      setSign:    (patch) => set((s) => ({ sign:    { ...s.sign,    ...patch } })),
      setTile:    (patch) => set((s) => ({ tile:    { ...s.tile,    ...patch } })),
      setCut:     (patch) => set((s) => ({ cut:     { ...s.cut,     ...patch } })),
      setOutput:  (patch) => set((s) => ({ output:  { ...s.output,  ...patch } })),
      setView:    (view)  => set({ view, zoom: "fit", pan: { x: 0, y: 0 } }),

      setSource: (source) => set({ source }),
      setZoom: (zoom) => set({ zoom }),
      setPan: (pan) => set({ pan }),
      resetViewport: () => set({ zoom: "fit", pan: { x: 0, y: 0 } }),
      setHostSize: (hostSize) => set({ hostSize }),

      zoomAt: (factor, mx, my) => {
        const s = get();
        if (!s.source) return;
        const layout = buildLayout(s.paper, s.margins, s.sign, s.tile);
        const { w: contentW, h: contentH } = contentSize(layout, s.view, s.sign);
        const { w: hostW, h: hostH } = s.hostSize;
        if (hostW === 0 || hostH === 0) return;
        const topInset = NAVBAR_HEIGHT;
        const visibleH = Math.max(1, hostH - topInset);
        const fitScale = computeFitScale(hostW, visibleH, contentW, contentH);
        const oldScale = s.zoom === "fit" ? fitScale : s.zoom;
        const newScale = clamp(oldScale * factor, fitScale * 0.1, fitScale * 40);
        if (newScale === oldScale) return;
        const oldOriginX = (hostW - contentW * oldScale) / 2 + s.pan.x;
        const oldOriginY = topInset + (visibleH - contentH * oldScale) / 2 + s.pan.y;
        const mmX = (mx - oldOriginX) / oldScale;
        const mmY = (my - oldOriginY) / oldScale;
        set({
          zoom: newScale,
          pan: {
            x: mx - mmX * newScale - (hostW - contentW * newScale) / 2,
            y: my - mmY * newScale - (topInset + (visibleH - contentH * newScale) / 2),
          },
        });
      },
      zoomCentered: (factor) => {
        const { hostSize, zoomAt } = get();
        zoomAt(factor, hostSize.w / 2, hostSize.h / 2);
      },

      setBusy: (text) =>
        set({ busy: text == null ? { active: false, text: "" } : { active: true, text } }),

      resetAll: () =>
        set((s) => ({
          paper:   { ...DEFAULT_PAPER },
          margins: { ...DEFAULT_MARGINS },
          sign:    s.source
            ? { ...DEFAULT_SIGN, width: s.source.nativeWidthMm, height: s.source.nativeHeightMm }
            : { ...DEFAULT_SIGN },
          tile:    { ...DEFAULT_TILE },
          cut:     { ...DEFAULT_CUT },
          output:  { ...DEFAULT_OUTPUT },
          view:    DEFAULT_VIEW,
          zoom:    "fit",
          pan:     { x: 0, y: 0 },
        })),
    }),
    {
      name: PERSIST_KEY,
      version: 1,
      partialize: (s) => ({
        paper:   s.paper,
        margins: s.margins,
        sign:    { ...DEFAULT_SIGN, lockAspect: s.sign.lockAspect },
        tile:    { ...DEFAULT_TILE, auto: s.tile.auto },
        cut:     s.cut,
        output:  s.output,
        view:    s.view,
      }),
    },
  ),
);
